import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import Skeleton from 'components/ui/skeleton';
import Switch from 'components/ui/switch';
import { useReminderSettings, useUpdateReminder, type ReminderDelivery } from 'modules/studyPlan';
import { messageLanguage } from 'modules/studyPlan/labels';

type Feedback = ReminderDelivery | 'NOT_LINKED' | 'ERROR' | null;

const FEEDBACK_KEYS: Record<Exclude<Feedback, null>, string> = {
  SENT: 'studyPlan.reminder_sent_note',
  BLOCKED: 'studyPlan.reminder_blocked',
  FAILED: 'studyPlan.reminder_failed',
  UNAVAILABLE: 'studyPlan.reminder_unavailable',
  NOT_LINKED: 'studyPlan.reminder_not_linked',
  ERROR: 'studyPlan.reminder_error',
};

const clock = (hour: number) => `${String(hour).padStart(2, '0')}:00`;
const isNotLinked = (error: unknown) =>
  (error as { response?: { data?: { code?: string } } } | null)?.response?.data?.code === 'TELEGRAM_NOT_LINKED';

const linkClass = 'font-medium text-foreground underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function BotLink({ botUsername }: { botUsername: string | null }) {
  const { t } = useTranslation();
  if (!botUsername) return null;
  return (
    <a href={`https://t.me/${encodeURIComponent(botUsername)}`} target="_blank" rel="noreferrer" className={linkClass}>
      {t('studyPlan.reminder_open_bot')}
    </a>
  );
}

/** Settings > Study plan: a daily Telegram message that opens the day's test. Off until the learner turns it on. */
export function ReminderCard() {
  const { t, i18n } = useTranslation();
  const { data: settings, isLoading, isError, refetch } = useReminderSettings();
  const update = useUpdateReminder();
  const [picked, setPicked] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const language = messageLanguage(i18n?.language);

  // A change made elsewhere (or a reload) replaces the choice made here.
  useEffect(() => setPicked(null), [settings?.hour, settings?.enabled]);

  // Waiting for a signed-in user is not a failure to load.
  if (isLoading || (!settings && !isError)) return <Skeleton className="h-44 w-full rounded-lg" aria-hidden="true" />;
  if (isError || !settings) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-5 py-4">
        <span className="inline-flex items-center gap-2 text-[13px] text-error-700 dark:text-error-400">
          <AlertCircle size={16} aria-hidden="true" />
          {t('failed_to_load')}
        </span>
        <Button variant="outline" size="xs" onClick={() => refetch()}>{t('retry')}</Button>
      </div>
    );
  }
  // A server that cannot send would only offer a switch that never works.
  if (!settings.available) return null;

  const hour = picked ?? settings.hour;
  const busy = update.isPending;

  const send = async (change: Parameters<typeof update.mutateAsync>[0], turningOn = false) => {
    setFeedback(null);
    try {
      const result = await update.mutateAsync(change);
      if (turningOn) setFeedback(result.delivery);
    } catch (error) {
      // A change that did not save leaves the saved hour as the one that counts.
      setPicked(null);
      if (isNotLinked(error)) {
        setFeedback('NOT_LINKED');
        void refetch();
      } else {
        setFeedback('ERROR');
      }
    }
  };

  const choose = (next: number) => {
    setPicked(next);
    // Once on, the hour is a live setting; while off it is only remembered for turning on.
    if (settings.enabled && next !== settings.hour) void send({ hour: next, language });
  };

  const notice = feedback ?? (settings.stoppedReason === 'BLOCKED' ? 'BLOCKED' : null);
  const blocked = notice === 'BLOCKED';

  return (
    <section aria-labelledby="plan-reminder" className="rounded-lg border border-border bg-card p-5">
      <h2 id="plan-reminder" className="text-[15px] font-semibold text-foreground">{t('studyPlan.reminder_title')}</h2>

      {!settings.telegramLinked ? (
        <div className="mt-2 space-y-2 text-sm text-muted-foreground">
          <p>{t('studyPlan.reminder_not_linked')}</p>
          <BotLink botUsername={settings.botUsername} />
        </div>
      ) : (
        <>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {settings.enabled ? t('studyPlan.reminder_status_on', { time: clock(settings.hour) }) : t('studyPlan.reminder_intro')}
          </p>

          <div className="mt-4 flex flex-col gap-2 sm:max-w-[220px]">
            <Label htmlFor="reminder-hour" className="text-sm font-medium text-foreground/90">{t('studyPlan.reminder_hour_label')}</Label>
            {/* Left enabled while a request is in flight (a disabled control would drop keyboard focus); choosing does nothing meanwhile. */}
            <Select value={String(hour)} onValueChange={(value) => !busy && choose(Number(value))}>
              <SelectTrigger id="reminder-hour" className={cn('tabular-nums', busy && 'opacity-60')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {settings.hours.map((option) => (
                  <SelectItem key={option} value={String(option)} className="tabular-nums">{clock(option)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4">
            <Switch
              checked={settings.enabled}
              disabled={busy}
              label={t('studyPlan.reminder_switch')}
              onChange={(on) => (on ? send({ enabled: true, hour, language }, true) : send({ enabled: false }))}
            />
          </div>

          {settings.stoppedReason === 'INACTIVE' && !feedback && (
            <p className="mt-3 text-[13px] text-muted-foreground">{t('studyPlan.reminder_inactive')}</p>
          )}
          {/* One live region that is always there, so a change to its text is announced. */}
          <p role="status" aria-live="polite" className={cn('mt-3 text-[13px]', notice === 'SENT' || !notice ? 'text-muted-foreground' : 'text-error-700 dark:text-error-400')}>
            {notice ? <>{t(FEEDBACK_KEYS[notice])} {blocked && <BotLink botUsername={settings.botUsername} />}</> : null}
          </p>
        </>
      )}
    </section>
  );
}

export default ReminderCard;
